import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, FileText, Plus, TrendingUp, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({
    tools: 0,
    blogs: 0
  });
  const [recentTools, setRecentTools] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toolsRes, blogsRes] = await Promise.all([
          axios.get(`${API}/tools`),
          axios.get(`${API}/blogs`)
        ]);
        
        setStats({
          tools: toolsRes.data.length,
          blogs: blogsRes.data.length
        });
        setRecentTools(toolsRes.data.slice(0, 5));
        setRecentBlogs(blogsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">Welcome to your admin dashboard</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-emerald-600" />
            </div>
            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
              Total
            </span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats.tools}</h3>
          <p className="text-sm text-gray-500">Tools Listed</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              Total
            </span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">{stats.blogs}</h3>
          <p className="text-sm text-gray-500">Blog Posts</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
              This month
            </span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">150K+</h3>
          <p className="text-sm text-gray-500">Page Views</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
              Growth
            </span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">+24%</h3>
          <p className="text-sm text-gray-500">vs Last Month</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Button asChild className="bg-emerald-500 hover:bg-emerald-600 h-auto py-4">
              <Link to="/admin/tools/new">
                <Plus className="w-5 h-5 mr-2" />
                Add New Tool
              </Link>
            </Button>
            <Button asChild className="bg-blue-500 hover:bg-blue-600 h-auto py-4">
              <Link to="/admin/blogs/new">
                <Plus className="w-5 h-5 mr-2" />
                Write Blog Post
              </Link>
            </Button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
          <h2 className="text-lg font-semibold mb-2">Tip of the Day</h2>
          <p className="text-emerald-100 mb-4">
            Keep your tool listings up to date with the latest pricing and features 
            to help users make better decisions.
          </p>
          <Button asChild variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
            <Link to="/admin/tools">Manage Tools</Link>
          </Button>
        </div>
      </div>

      {/* Recent Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tools */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Tools</h2>
            <Link to="/admin/tools" className="text-sm text-emerald-600 hover:text-emerald-700">
              View all
            </Link>
          </div>
          {recentTools.length > 0 ? (
            <div className="space-y-3">
              {recentTools.map((tool) => (
                <div key={tool.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img src={tool.logo} alt={tool.name} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{tool.name}</p>
                    <p className="text-xs text-gray-500">{tool.category}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No tools yet. Add your first tool!</p>
          )}
        </div>

        {/* Recent Blogs */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Blog Posts</h2>
            <Link to="/admin/blogs" className="text-sm text-emerald-600 hover:text-emerald-700">
              View all
            </Link>
          </div>
          {recentBlogs.length > 0 ? (
            <div className="space-y-3">
              {recentBlogs.map((blog) => (
                <div key={blog.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img src={blog.image} alt={blog.title} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{blog.title}</p>
                    <p className="text-xs text-gray-500">{blog.date}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No blog posts yet. Write your first post!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;